import React, { useEffect, useState } from "react";
import { fetchJson } from "../../services/api";
import {
  PageHeader,
  SectionCard,
  Loader,
  ErrorMsg,
  refreshBtnStyle,
} from "../components/Common";
import "./VMOverviewPage.css";

interface VMOverviewData {
  host: string;
  uptime: string;

  cpu_usage_pct: number;
  cpu_cores: number;

  memory_total_mb: number;
  memory_used_mb: number;
  memory_free_mb: number;

  disk_total_gb: number;
  disk_used_gb: number;

  docker_containers: number;
  running_containers: number;
  stopped_containers: number;

  top_processes: {
    user: string;
    cpu: number;
    mem: number;
    command: string;
    pid: string;
    vsz: string;
    rss: string;
    tty: string;
    stat: string;
    start: string;
    time: string;
  }[];
}

const getProcessStateTooltip = (stat: string) => {
  const value = (stat || "").trim();

  if (!value) {
    return "No process state information available.";
  }

  const meanings: string[] = [];

  for (const char of value) {
    const meaning = {
      R: "Running",
      S: "Sleeping (interruptible)",
      D: "Uninterruptible sleep (usually I/O wait)",
      Z: "Zombie process",
      T: "Stopped",
      t: "Stopped by debugger",
      X: "Dead (should never be seen)",
      I: "Idle kernel thread",
      W: "Paging (older Linux kernels)",
      P: "Parked",
      s: "Session leader",
      l: "Multi-threaded",
      "+": "Foreground process group",
    }[char];

    meanings.push(
      meaning ? `${char} = ${meaning}` : `${char} = unknown status code`
    );
  }

  return [`STAT value: ${value}`, ...meanings].join("\n");
};

const VMOverviewPage: React.FC = () => {
  const [data, setData] = useState<VMOverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);

    fetchJson<VMOverviewData>("/monitor/vm/overview/")
      .then((res) => {
        setData(res);
      })
      .catch((e: any) => {
        setError(e.message || "Failed to load VM overview.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) return <Loader />;
  if (error) return <ErrorMsg msg={error} onRetry={load} />;
  if (!data) return null;

  const memoryPct =
    data.memory_total_mb > 0
      ? (data.memory_used_mb / data.memory_total_mb) * 100
      : 0;

  const diskPct =
    data.disk_total_gb > 0 ? (data.disk_used_gb / data.disk_total_gb) * 100 : 0;

return (
  <div>
    <PageHeader
      title="VM Overview"
      subtitle="Host-level utilization and Docker container statistics"
    />

    <div className="vm-overview-page__toolbar">
      <button onClick={load} style={refreshBtnStyle}>
        ↻ Refresh
      </button>
    </div>

    <SectionCard title="System Summary">
      <div className="vm-overview-page__summary-grid">
        <div className="vm-overview-page__metric-card">
          <div className="vm-overview-page__metric-label">Hostname</div>
          <div className="vm-overview-page__metric-value">{data.host}</div>
        </div>

        <div className="vm-overview-page__metric-card">
          <div className="vm-overview-page__metric-label">CPU Usage</div>
          <div
            className="vm-overview-page__metric-value"
            style={{ color: data.cpu_usage_pct > 80 ? "#ef4444" : data.cpu_usage_pct > 50 ? "#f59e0b" : "#22c55e" }}
          >
            {data.cpu_usage_pct}%
          </div>
        </div>

        <div className="vm-overview-page__metric-card">
          <div className="vm-overview-page__metric-label">CPU Cores</div>
          <div className="vm-overview-page__metric-value">{data.cpu_cores}</div>
        </div>

        <div className="vm-overview-page__metric-card">
          <div className="vm-overview-page__metric-label">Containers</div>
          <div className="vm-overview-page__metric-value" style={{ color: "#22c55e" }}>{data.docker_containers}</div>
        </div>
      </div>
    </SectionCard>

    <div className="vm-overview-page__metrics-grid">
      <SectionCard title="CPU Usage">
        <div
          className="vm-overview-page__metric-value--large"
          style={{ color: data.cpu_usage_pct > 80 ? "#ef4444" : data.cpu_usage_pct > 50 ? "#f59e0b" : "#22c55e" }}
        >
          {data.cpu_usage_pct}%
        </div>

        <div
          style={{
            marginTop: 10,
            color: "var(--muted)",
          }}
        >
          Current Processor Load
        </div>
      </SectionCard>

      <SectionCard title="Memory Usage">
        <div className="vm-overview-page__metric-value--large" style={{ color: "#3b82f6" }}>
          {data.memory_used_mb} MB
        </div>

        <div className="vm-overview-page__metric-footer">Used of {data.memory_total_mb} MB</div>

        <div className="vm-overview-page__progress">
          <div className="vm-overview-page__progress-bar" style={{ width: `${memoryPct}%`, background: "#3b82f6" }} />
        </div>

        <div className="vm-overview-page__metric-footer">
          Free: {data.memory_free_mb} MB
        </div>
      </SectionCard>

      <SectionCard title="Disk Usage">
        <div className="vm-overview-page__metric-value--large" style={{ color: "#f59e0b" }}>
          {data.disk_used_gb} GB
        </div>

        <div className="vm-overview-page__metric-footer">Used of {data.disk_total_gb} GB</div>

        <div className="vm-overview-page__progress">
          <div className="vm-overview-page__progress-bar" style={{ width: `${diskPct}%`, background: "#f59e0b" }} />
        </div>
      </SectionCard>

      <SectionCard title="Docker Containers">
        <div className="vm-overview-page__metric-value--large" style={{ color: "#22c55e" }}>
          {data.docker_containers}
        </div>

        <div style={{ marginTop: 12 }}>
          <div
            style={{
              color: "#22c55e",
              fontWeight: 600,
            }}
          >
            ● Running: {data.running_containers}
          </div>

          <div
            style={{
              color: "#ef4444",
              fontWeight: 600,
              marginTop: 8,
            }}
          >
            ● Stopped: {data.stopped_containers}
          </div>
        </div>
      </SectionCard>

      <SectionCard title="System Health">
        <div
          className="vm-overview-page__metric-value"
          style={{ fontSize: 30, fontWeight: 700, color: data.cpu_usage_pct > 80 ? "#ef4444" : "#22c55e" }}
        >
          {data.cpu_usage_pct > 80
            ? "Critical"
            : "Healthy"}
        </div>

        <div className="vm-overview-page__metric-footer" style={{ marginTop: 12 }}>
          CPU: {data.cpu_usage_pct}%
        </div>

        <div className="vm-overview-page__metric-footer" style={{ marginTop: 6 }}>
          Containers: {data.running_containers}/{data.docker_containers}
        </div>

        <div className="vm-overview-page__metric-footer" style={{ marginTop: 6 }}>
          Uptime: {data.uptime}
        </div>
      </SectionCard>

      <SectionCard title="Top CPU Consumer">
        <div className="vm-overview-page__table-cell--break" style={{ fontSize: 18, fontWeight: 700 }}>
          {data.top_processes?.[0]?.command}
        </div>

        <div
          style={{
            marginTop: 12,
            color: "#ef4444",
            fontWeight: 700,
          }}
        >
          CPU: {data.top_processes?.[0]?.cpu}%
        </div>
      </SectionCard>
    </div>

    <div style={{ marginTop: 20 }}>
      <SectionCard title="Top CPU Consuming Processes">
        <div className="vm-overview-page__table-wrap">
          <table className="vm-overview-page__table">
            <thead className="vm-overview-page__table-head">
              <tr>
                <th title="Linux user account that started the process" className="vm-overview-page__table-head-cell">User</th>
                <th title="CPU usage percentage for this process" className="vm-overview-page__table-head-cell">CPU %</th>
                <th title="Memory usage percentage for this process" className="vm-overview-page__table-head-cell">Memory %</th>
                <th title="Full command line for the running process" className="vm-overview-page__table-head-cell">Command</th>
                <th title="Process identifier number" className="vm-overview-page__table-head-cell">PID</th>
                <th title="Virtual memory size used by the process" className="vm-overview-page__table-head-cell">VSZ</th>
                <th title="Resident memory size currently in RAM" className="vm-overview-page__table-head-cell">RSS</th>
                <th title="Terminal controlling the process" className="vm-overview-page__table-head-cell">TTY</th>
                <th title="Process state flags such as running or sleeping" className="vm-overview-page__table-head-cell">STAT</th>
                <th title="When the process started" className="vm-overview-page__table-head-cell">START</th>
                <th title="Total CPU time consumed by the process" className="vm-overview-page__table-head-cell">TIME</th>
              </tr>
            </thead>

            <tbody>
              {data.top_processes?.map((p, index) => (
                <tr key={index} className="vm-overview-page__table-row">
                  <td className="vm-overview-page__table-cell">{p.user}</td>
                  <td className="vm-overview-page__table-cell">{p.cpu}</td>
                  <td className="vm-overview-page__table-cell">{p.mem}</td>
                  <td className="vm-overview-page__table-cell vm-overview-page__table-cell--mono">{p.command}</td>
                  <td className="vm-overview-page__table-cell">{p.pid}</td>
                  <td className="vm-overview-page__table-cell">{p.vsz}</td>
                  <td className="vm-overview-page__table-cell">{p.rss}</td>
                  <td className="vm-overview-page__table-cell">{p.tty}</td>
                  <td title={getProcessStateTooltip(p.stat)} className="vm-overview-page__table-cell">{p.stat}</td>
                  <td className="vm-overview-page__table-cell">{p.start}</td>
                  <td className="vm-overview-page__table-cell">{p.time}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  </div>
);
};

export default VMOverviewPage;
